import { html } from 'lit-element';
import { nothing } from 'lit-html';
import { ifNotNull } from '../../utils/directives';
import {
  KatLitElement,
  KatalComponent,
  Keys,
  coerceToArray,
  booleanConverter,
  nextUniqueId,
} from '../../shared/base';
import { fitHandler } from '../dropdown/fit-handler';

const spaceKey = Keys.Space;
const enterKey = Keys.Enter;
const downArrowKey = Keys.ArrowDown;
const upArrowKey = Keys.ArrowUp;
const tabKey = Keys.Tab;

/**
 * @component {kat-predictive-input} KatalPredictiveinput Predictive Input will filter the attached list while the user is typing
 * @guideline Do Stringify data object when passing it to attribute data
 * @example Basic {"value": "Test", "label":"Predictive Input", "options":["Test","Test Name","Katal","CXD","Flo"] }
 * @example WithTooltip {"label": "With Tooltip", "tooltip-text": "A tooltip with some additional information", "tooltip-position": "right"}
 * @event {{ value: string }} change This event is fired when focus on input is gone AND value is different. Detail object contains current input value
 * @event {{ value: string }} blur This event is fired whenever the inner input element fires blur. Detail object contains inner input value BEFORE focus is gone
 * @event mouseup This event is fired whenever the inner input element fires mouseup. Detail object is unavailable for this event
 * @event input This event is fired whenever the inner input element fires input. Detail object is unavailable for this event
 * @event {{ value: string }} focus This event is fired whenever the inner input element fires focus. Detail object contains current input value
 * @event keydown This event is fired whenever the inner input element fires keydown. Detail object is unavailable for this event
 * @event keyup This event is fired whenever the inner input element fires keyup. Detail object is unavailable for this event
 * @event {{ expanded: boolean }} expanded This event is fired whenever the dropdown is expanded/collapsed. Detail object contains `expanded` boolean property
 * @theme flo
 * @status Production
 */
export class KatalPredictiveinput extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {number} maxlength Specifies the max length of characters allowed in the input
       */
      maxlength: { type: Number, reflect: true },

      /**
       * @classprop {boolean} readonly Specifies that the element is read only
       */
      readonly: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {boolean} autofocus Specifies that the element should receive focus by default
       */
      autofocus: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {boolean} required Specifies that the element should receive focus by default
       */
      required: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {enum} filter-type Sets how the list will be filtered
       * @enum {value} contains Will filter if the result contains the criteria
       * @enum {value} startsWith Will filter if the result starts with the criteria
       */
      filterType: { type: String, attribute: 'filter-type', reflect: true },

      /**
       * @classprop {json} options Array of `Option` strings, e.g. ["test","value","testValue","icon"]
       * @jsonExample - {"options": ["Test","Test Name","Katal","CXD","Flo"]}
       * @required
       */
      options: { type: Array },

      /**
       * @classprop {string} placeholder The text that will be displayed to the user as a placeholder.
       */
      placeholder: { type: String, reflect: true },

      /**
       * @classprop {string} max-height The max height of the selection area.
       */
      maxHeight: { type: String, attribute: 'max-height', reflect: true },

      /**
       * @classprop {string} label The optional field label.
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} name The optional field name, hidden from view.
       */
      name: { type: String, reflect: true },

      /**
       * @classprop {boolean} expanded Whether the component is currently showing its dropdown options.
       */
      expanded: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {string} value The value of the selected option.
       */
      value: { type: String, reflect: true },

      /**
       * @classprop {enum} size The size of the selected option.
       * @enum {value} large Large select box - Default
       * @enum {value} small Small select box
       */
      size: { type: String, reflect: true },

      /**
       * @classprop {boolean} disabled Setting the select box to disabled will stop its interactivity.
       */
      disabled: { converter: booleanConverter, reflect: true },

      /**
       * @classprop {string} unique-id Used to associate an external label to an input.  See the kat-label for attribute.
       */
      uniqueId: { type: String, attribute: 'unique-id', reflect: true },

      /**
       * @classprop {string} constraint-emphasis Optional pretext for the constraint label.  Used to provide addtional context to the constraint for the input
       */
      constraintEmphasis: {
        type: String,
        attribute: 'constraint-emphasis',
        reflect: true,
      },

      /**
       * @classprop {string} constraint-label Provides users with more information about what they enter into the input
       */
      constraintLabel: {
        type: String,
        attribute: 'constraint-label',
        reflect: true,
      },

      /**
       * @classprop {enum} state Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
       * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
       */
      state: { type: String, reflect: true },

      /**
       * @classprop {string} state-emphasis Optional pretext for the state label.  Used to provide addtional context to the state for the input
       */
      stateEmphasis: {
        type: String,
        attribute: 'state-emphasis',
        reflect: true,
      },

      /**
       * @classprop {string} state-label Provides users with more information about why the input is in the state its in
       */
      stateLabel: { type: String, attribute: 'state-label', reflect: true },

      /**
       * @classprop {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        converter: booleanConverter,
        attribute: 'katal-1-2-spacing-fixes',
        reflect: true,
      },

      /**
       * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
       * set.
       */
      tooltipText: { type: String, attribute: 'tooltip-text', reflect: true },

      /**
       * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
       */
      tooltipPosition: {
        type: String,
        attribute: 'tooltip-position',
        reflect: true,
      },

      /**
       * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
       */
      tooltipTriggerIcon: {
        type: String,
        attribute: 'tooltip-trigger-icon',
        reflect: true,
      },

      /**
       * @classprop {string} autocomplete Specifies the autocomplete behaviour that a browser should attempt:
       * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
       */
      autocomplete: { type: String, reflect: true },
    };
  }

  constructor() {
    super();

    this._uniqueId = nextUniqueId();

    this._options = [];
    this.size = 'large';
    this.uniqueId = this._uniqueId;
    this.filterType = 'contains';
    this.tooltipPosition = 'top';
    this.tooltipTriggerIcon = 'help_outline';
  }

  get options() {
    return this._options;
  }

  /**
   * @deprecated
   * This logic will be removed in 1.3
   */
  set options(val) {
    const oldVal = this.options;
    this._options = coerceToArray(this, 'options', val);
    this.requestUpdate('options', oldVal);
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    this.createEvents();
  }

  updated(changedProperties) {
    if (changedProperties.has('expanded')) {
      this.setOrUnsetOptionsContainerBottomOffset();
    }
  }

  renderOptions() {
    return this.filterOptions().map(
      (option, index) =>
        html`
          <div class="option-row " id="${option + index}" tabindex="-1">
            <div class="option" data-value="${option}" data-name="${option}">
              ${option}
            </div>
          </div>
        `
    );
  }

  render() {
    const optionDomOptions = this.renderOptions();

    const formLabel = this.label
      ? html`
          <kat-label
            class="form-label"
            for="${this.uniqueId}"
            text="${ifNotNull(this.label)}"
            tooltip-text="${ifNotNull(this.tooltipText)}"
            tooltip-position="${this.tooltipPosition}"
            tooltip-trigger-icon="${this.tooltipTriggerIcon}"
          ></kat-label>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <kat-label
            class="constraint-label"
            for="${this.uniqueId}"
            variant="constraint"
            emphasis="${ifNotNull(this.constraintEmphasis)}"
            text="${this.constraintLabel}"
          ></kat-label>
        `
      : nothing;

    const stateLabel =
      this.stateLabel || this.stateEmphasis
        ? html`
            <kat-label
              class="state-label"
              variant="constraint"
              text="${ifNotNull(this.stateLabel)}"
              emphasis="${ifNotNull(this.stateEmphasis)}"
              state="${this.state}"
            ></kat-label>
          `
        : nothing;

    let inputClass = 'header-row-text value ';
    if (this.state) {
      inputClass += this.state;
    }

    const containerClass = 'kat-select-container ' + this.size;

    return html`
      ${formLabel}
      <div class="${containerClass}" @change=${this.selectContainerChange}>
        <div class="select-header">
          <div class="header-row">
            <input
              class="${inputClass}"
              id="${this.uniqueId}"
              type="text"
              name="${ifNotNull(this.name)}"
              placeholder="${ifNotNull(this.placeholder)}"
              .value=${ifNotNull(this.value)}
              maxlength="${ifNotNull(this.maxlength)}"
              autocomplete=${ifNotNull(this.autocomplete)}
              ?disabled="${this.disabled}"
              ?readonly="${this.readonly}"
              ?autofocus="${this.autofocus}"
              ?required="${this.required}"
              @focus=${this.inputFocus}
              @blur=${this.inputBlur}
              @keydown=${this.inputKeyPress}
              @keyup=${this.inputKeyPress}
              @mouseup=${this.supportedBubblingEvent}
              @input=${this.supportedBubblingEvent}
            />
          </div>
        </div>
        <div
          class="select-options"
          @click=${this.selectOptionClick}
          @keydown=${this.selectOptionKeydown}
        >
          <div
            class="option-inner-container"
            style="${ifNotNull(
              this.maxHeight &&
                'max-height:' + this.maxHeight + ';overflow:auto;'
            )}"
          >
            ${optionDomOptions}
          </div>
        </div>
      </div>
      ${constraintLabel} ${stateLabel}
    `;
  }

  selectContainerChange(e) {
    e.stopPropagation();
    const container = e.target;
    let debounce = true;
    const debounceFunction = function(event) {
      if (
        event.relatedTarget !== null &&
        event.relatedTarget.classList.contains('option-row')
      ) {
        debounce = false;
      }
    };
    // This debounce is for the unique case of when the use has typed into the input then pressed the down arrow to go into the dropdown tray
    // this would cause both change and focus out to occur - without the debounce it will close the tray when it should be open allowing navigating between options
    container.addEventListener('focusout', debounceFunction);
    setTimeout(() => {
      if (debounce) {
        this.value = e.detail ? e.detail.value : e.target.value;
        const evt = new CustomEvent('change', {
          bubbles: true,
          detail: { value: this.value },
        });
        this.dispatchEvent(evt);
      }
      container.removeEventListener('focusout', debounceFunction);
    }, 20);
  }

  selectOptionClick(event) {
    if (!this.disabled) {
      this.selectOption(event.target);
    }
  }

  selectOptionKeydown(event) {
    const option = event.target.querySelector('.option');
    const target = event.target;

    if (!this.disabled && option) {
      if (event.keyCode === spaceKey || event.keyCode === enterKey) {
        event.preventDefault();
        this.selectOption(option);
      } else if (event.keyCode === upArrowKey) {
        //previous
        event.preventDefault();
        const prevEl = target.previousElementSibling;
        if (prevEl) {
          prevEl.focus();
        } else {
          const katSelectContainer =
            target.parentElement.parentElement.parentElement;
          const input = katSelectContainer.children[0].children[0].children[0];
          input.focus();
        }
      } else if (event.keyCode === downArrowKey) {
        //next
        event.preventDefault();
        const nextEl = target.nextElementSibling;
        if (nextEl) {
          nextEl.focus();
        }
      }
    }
  }

  inputKeyPress(e) {
    const keyCode = e.keyCode || e.which;

    if (keyCode === downArrowKey && this.options.length > 0) {
      this.querySelector('.option-row').focus();
    } else if (keyCode !== tabKey) {
      this.value = e.target.value;
    }
  }

  inputFocus(e) {
    const container = this.querySelector('.kat-select-container');
    const newFocus = e.relatedTarget;
    if (!this.disabled && !container.contains(newFocus)) {
      this.toggleExpanded();
    }

    // Although 'focus' is not a custom event, it doesn't bubble up to parent elements.
    // Likewise, we will synthetically fire 'focus' and not have it bubble.
    this.dispatchCustomEvent('focus', { value: this.value }, false);
  }

  inputBlur() {
    // Although 'blur' is not a custom event, it doesn't bubble up to parent elements.
    // Likewise, we will synthetically fire 'focus' and not have it bubble.
    this.dispatchCustomEvent('blur', { value: this.value }, false);
  }

  supportedBubblingEvent(e) {
    // No-op, just here as to say we are officially aware
    // these events bubble up and we support their usage.
  }

  async toggleExpanded() {
    this.expanded = !this.expanded;
    await this.updateComplete;
    this.dispatchCustomEvent('expanded', { expanded: this.expanded }, false);
  }

  fitDropDownWindow() {
    const optionContainer = this.querySelector('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );

    optionContainer.style.display = 'table';
    fitHandler(this, optionInnerContainer);
    optionContainer.style.removeProperty('display');
  }

  setOrUnsetOptionsContainerBottomOffset() {
    const selectOptionsElement = this.querySelector('.select-options');

    if (!selectOptionsElement) {
      return;
    }

    this.fitDropDownWindow();

    if (this.hasAttribute('drop-up')) {
      const offset = this.querySelector('div.select-header').offsetHeight;

      selectOptionsElement.setAttribute('style', `bottom: ${offset}px`);
    } else {
      selectOptionsElement.removeAttribute('style');
    }
  }

  selectOption(element) {
    const intialValue = this.value;
    const newValue = element.dataset.value;
    if (intialValue !== newValue) {
      this.value = newValue;
      this.dispatchCustomEvent('change', { value: this.value });
    }

    this.expanded = false;
  }

  filterOptions() {
    if (!this.value) {
      return this.options;
    }

    const filter =
      this.filterType === 'startsWith'
        ? o => o.toLowerCase().startsWith(this.value.toLowerCase())
        : o => o.toLowerCase().indexOf(this.value.toLowerCase()) !== -1;

    return this.options.filter(filter);
  }

  createEvents() {
    this.addEventListener('focusout', e => {
      const container = this.querySelector('.kat-select-container');
      const newFocus = e.relatedTarget;
      if (!this.disabled && !container.contains(newFocus)) {
        this.expanded = false;
      }
    });
  }
}

KatalComponent.register('kat-predictive-input', KatalPredictiveinput);
